import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Line } from "@nova/protobuf"
import GeometryLayer, { GeometryMode } from "plugins/shared/layers/GeometryLayer.class"
import {
    selectFirePerimetersToDraw,
    selectSelectedFirePerimeterId,
    MOCKED_PERIMETER_ID,
    selectMockedPerimeterName,
    selectEditingData,
    moveFirePerimeterToEditable,
    clearEditingData,
    selectUpdateMethod,
} from "../FirePerimeterSlice"
import { DeckGLGeometry, Geometry } from "plugins/shared/layers/types"
import { useMemo, useState } from "react"
import { FirePerimeterMethod, LineWidth, ToolName } from "@nova/constants"
import {
    selectFirePerimeterById,
    selectFirePerimetersMap,
    selectNextFirePerimeterNumber,
} from "features/entity-caching/fire-perimeters/selectors"
import { decodeNumberToRGBA } from "@nova/serializer/src/client/util"
import { defaultFirePerimeterColorDeckGL } from "../constants"
import {
    createPerimeter,
    deletePerimeter,
    updatePerimeter,
} from "features/pubsub/entityActions/cubes/perimeter"
import { geometryDataStore, type GeometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { selectUserID } from "user/profile/selectors"
import { arePolygonsEqual, calculateArea } from "@nova/calculate-geospatial"
import { PolygonGeometry } from "plugins/shared/layers/GeometryLinesLayer.class"
import { useNonReactiveCallback } from "@nova/use-non-reactive-callback"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { selectCurrentGeometryToolType } from "plugins/shared/geometry/SharedGeometrySlice"
import { mergePolygonsIfTheyIntersect } from "@nova/calculate-union"
import {
    clearSelection,
    selectLockedLayersMap,
    selectSingleLayer,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"

export const useNormalPerimeterLayer = () => {
    const dispatch = useAppDispatch()
    const perimeters = useAppSelector(selectFirePerimetersToDraw)
    const editingData = useAppSelector(selectEditingData())
    const filteredPerimeters = useMemo(() => {
        return perimeters
            .filter((e) => !(e.id === editingData?.id && e.idx === editingData?.polygonIndex))
            .map((e, i) => ({ ...e, deckGlIdx: i }))
            .sort(
                (a, b) => calculateArea(a.coordinates) - calculateArea(b.coordinates)
            ) as PolygonGeometry[]
    }, [perimeters, editingData])
    const updateMethod = useAppSelector(selectUpdateMethod())
    const firePerimeterMap = useAppSelector(selectFirePerimetersMap)
    const selectedFirePerimeterId = useAppSelector(selectSelectedFirePerimeterId)
    const selectedFirePerimeter = useAppSelector(
        selectFirePerimeterById(selectedFirePerimeterId || "")
    )
    const [hoveredId, setHoveredId] = useState({ id: "", index: -1 })
    const pdfSnapshotOpen = useAppSelector(selectSnapshotOpen())
    const currentGeometryToolType = useAppSelector(selectCurrentGeometryToolType)
    const someGeometryToolIsInUse = !!currentGeometryToolType
    const lockedLayersMap = useAppSelector(selectLockedLayersMap)

    /**
     * Waterfalls color preference in the following order:
     * 1. Geometry's own color
     * 2. Fire perimeter to which the geometry belongs to's color
     * 3. Selected fire perimeter's color
     * 4. Default color
     */
    const generateColor = (geometry?: DeckGLGeometry) => {
        return geometry?.color !== undefined
            ? decodeNumberToRGBA(geometry.color)
            : geometry?.id && firePerimeterMap[geometry.id]?.outline
              ? decodeNumberToRGBA(firePerimeterMap[geometry.id].outline!)
              : selectedFirePerimeter &&
                  geometry?.id === selectedFirePerimeter.id &&
                  selectedFirePerimeter.outline
                ? decodeNumberToRGBA(selectedFirePerimeter.outline)
                : defaultFirePerimeterColorDeckGL
    }

    if (filteredPerimeters.length === 0) return []

    return new GeometryLayer({
        id: `fire-perimeter-geometry`,
        data: filteredPerimeters,
        visible: true,
        pickable:
            !!selectedFirePerimeter &&
            updateMethod !== FirePerimeterMethod.Manual &&
            !pdfSnapshotOpen,
        selectedId: selectedFirePerimeterId,
        mode: GeometryMode.MultiPolygon,
        onHover: (info) => {
            if (!info.object?.id || lockedLayersMap[info.object.id]) return
            if (info.object.id === selectedFirePerimeterId && !someGeometryToolIsInUse) {
                const isNew = !(
                    info.object?.id === hoveredId.id && info.object?.idx === hoveredId.index
                )
                if (isNew) {
                    setHoveredId({ id: info.object?.id ?? "", index: info.object?.idx ?? -1 })
                }
            }
        },
        initEdit: (id, idx) => {
            const isEditingSamePerimeter =
                currentGeometryToolType === ToolName.FirePerimeter && id === editingData?.id
            if (someGeometryToolIsInUse && !isEditingSamePerimeter) return

            if (lockedLayersMap[id]) {
                dispatch(clearSelection())
                dispatch(setActiveLayerId({ id: undefined }))
                return
            }
            const perimeter = firePerimeterMap[id]
            if (perimeter) {
                geometryDataStore.free(true)
                dispatch(selectSingleLayer({ id: perimeter.id }))
                dispatch(setActiveLayerId({ id: perimeter.id }))
                dispatch(
                    moveFirePerimeterToEditable({
                        perimeter,
                        currentIndex: idx,
                    })
                )
            }
        },
        extraLayers: [],
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: generateColor(filteredPerimeters[0]),
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: [255, 255, 255],
                getFillColor: generateColor(filteredPerimeters[0]),
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: generateColor(filteredPerimeters[0]),
            },
            lines: {
                widthScale: 1,
                widthMaxPixels: 8,
                widthMinPixels: 2,
                capRounded: false,
                billboard: false,
                getColor: generateColor,
                getWidth: 2,
                miterLimit: 0,
            },
            polygons: {
                updateTriggers: {
                    getLineWidth: hoveredId,
                },
                stroked: true,
                getLineWidth: (d: Geometry & { idx: number }) => {
                    const isHovered = d.id === hoveredId.id && d.idx === hoveredId.index

                    if (d.id) {
                        const perimeter = firePerimeterMap[d.id]
                        if (perimeter) {
                            switch (perimeter.width as LineWidth) {
                                case LineWidth.thick:
                                    return isHovered ? 6 : 5
                                default:
                                    return isHovered ? 4 : 3
                            }
                        }
                    }

                    return 3
                },
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
                getLineColor: generateColor,
                getFillColor: [0, 0, 0, 0],
                getFillPattern: (d: Line) => d.polygonPattern || "default",
                getFillPatternOffset: [0, 0],
            },
        },
    })
}

export const useEditingPerimeterLayer = () => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID) || ""
    const editingData = useAppSelector(selectEditingData())
    const filteredPerimeters = useMemo(() => {
        return editingData?.mockedPerimeter
            ? [{ ...editingData.mockedPerimeter, deckGlIdx: 0 }]
            : []
    }, [editingData])
    const updateMethod = useAppSelector(selectUpdateMethod())
    const firePerimeterMap = useAppSelector(selectFirePerimetersMap)
    const selectedFirePerimeterId = useAppSelector(selectSelectedFirePerimeterId)
    const selectedFirePerimeter = useAppSelector(
        selectFirePerimeterById(selectedFirePerimeterId || "")
    )
    const editingPerimeter = useAppSelector(selectFirePerimeterById(editingData?.id || ""))
    const nextFirePerimeterName = "Fire Perimeter " + useAppSelector(selectNextFirePerimeterNumber)
    const mockedName = useAppSelector(selectMockedPerimeterName())

    /**
     * Waterfalls color preference in the following order:
     * 1. Geometry's own color
     * 2. Fire perimeter to which the geometry belongs to's color
     * 3. Selected fire perimeter's color
     * 4. Default color
     */
    const generateColor = (geometry?: DeckGLGeometry) => {
        return geometry?.color !== undefined
            ? decodeNumberToRGBA(geometry.color)
            : geometry?.id && firePerimeterMap[geometry.id]?.outline
              ? decodeNumberToRGBA(firePerimeterMap[geometry.id].outline!)
              : selectedFirePerimeter &&
                  geometry?.id === selectedFirePerimeter.id &&
                  selectedFirePerimeter.outline
                ? decodeNumberToRGBA(selectedFirePerimeter.outline)
                : defaultFirePerimeterColorDeckGL
    }

    const flushData = useNonReactiveCallback(
        async (store: GeometryDataStore, save: boolean) => {
            if (save) {
                if (
                    editingData?.id === MOCKED_PERIMETER_ID &&
                    store.polygon.coordinates.length >= 3
                ) {
                    dispatch(
                        createPerimeter({
                            data: {
                                name: mockedName || nextFirePerimeterName,
                                polygons: [{ coordinates: store.polygon.coordinates }],
                                createdBy: { userId },
                            },
                        })
                    )
                    dispatch(clearEditingData())
                } else if (
                    editingPerimeter &&
                    editingData &&
                    editingData.id !== MOCKED_PERIMETER_ID
                ) {
                    const polygons = [...editingPerimeter.polygons]
                    const index =
                        editingData.polygonIndex === -1 ? polygons.length : editingData.polygonIndex
                    polygons[index] = {
                        coordinates: store.exportToPolygons(),
                    }
                    const newPolygons = (
                        await mergePolygonsIfTheyIntersect(
                            polygons.map(({ coordinates }) => coordinates)
                        )
                    ).map((e) => ({ coordinates: e }))
                    if (newPolygons.length === 0) {
                        dispatch(deletePerimeter({ id: editingData.id }))
                    } else if (!arePolygonsEqual(editingPerimeter.polygons, newPolygons)) {
                        dispatch(
                            updatePerimeter({
                                id: editingData.id,
                                updates: { polygons: newPolygons },
                            })
                        )
                    }
                }
            }
        },
        [editingData, editingPerimeter, mockedName, nextFirePerimeterName]
    )

    if (filteredPerimeters.length === 0) return []

    return new GeometryLayer({
        id: `fire-perimeter-geometry-editing`,
        data: filteredPerimeters,
        visible: true,
        pickable: false,
        editData: filteredPerimeters.length
            ? {
                  canEdit: true,
                  flush: flushData,
              }
            : undefined,
        drawData: { drawMode: "smooth", canDraw: updateMethod === FirePerimeterMethod.Manual },
        selectedId: selectedFirePerimeterId,
        mode:
            updateMethod === FirePerimeterMethod.Manual
                ? GeometryMode.LineToPolygon
                : GeometryMode.MultiPolygon,
        canCloseLine: false,
        extraLayers: [],
        geometryName: "Perimeter",
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: generateColor(filteredPerimeters[0]),
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: [255, 255, 255],
                getFillColor: generateColor(filteredPerimeters[0]),
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: generateColor(filteredPerimeters[0]),
            },
            lines: {
                widthScale: 1,
                widthMaxPixels: 8,
                widthMinPixels: 2,
                capRounded: false,
                billboard: false,
                getColor: generateColor,
                getWidth: 2,
                miterLimit: 0,
            },
            polygons: {
                stroked: true,
                getLineWidth: (d: Geometry & { idx: number }) => {
                    const isHovered = false

                    if (d.id) {
                        const perimeter = firePerimeterMap[d.id]
                        if (perimeter) {
                            switch (perimeter.width as LineWidth) {
                                case LineWidth.thick:
                                    return isHovered ? 6 : 5
                                default:
                                    return isHovered ? 4 : 3
                            }
                        }
                    }

                    return 3
                },
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
                getLineColor: generateColor,
                getFillColor: [0, 0, 0, 0],
                getFillPattern: (d: Line) => d.polygonPattern || "default",
                getFillPatternOffset: [0, 0],
            },
        },
    })
}
